// Local catalog of the servers listed on https://mcpservers.org/, used by
// the Integrations search so queries matching an MCP server surface the MCP
// entry point. The site has no public search API, so the catalog is a
// build-time snapshot regenerated with
// `node scripts/crawl-mcpservers-catalog.mjs`.

export interface McpCatalogServer {
  /** Path under https://mcpservers.org/servers/. */
  slug: string;
  name: string;
  description: string;
}

let catalogPromise: Promise<McpCatalogServer[]> | undefined;

/**
 * Loads the catalog snapshot. The JSON is a separate lazy chunk (~2MB raw,
 * ~650KB over the wire), so only search actually pays for it, and only once.
 */
export const loadMcpServersCatalog = (): Promise<McpCatalogServer[]> => {
  catalogPromise ??= import("./mcp-servers-catalog.json").then(
    (module): McpCatalogServer[] => module.default
  );
  return catalogPromise;
};

/**
 * Case-insensitive substring search over server names and descriptions,
 * mirroring how the Integrations search matches integration cards. Name
 * matches rank before description-only matches so the servers shown on the
 * MCP card are the most recognizable ones. An empty or blank query matches
 * nothing: the catalog only ever adds results, it never gates the MCP card.
 */
export const searchMcpServersCatalog = (
  catalog: readonly McpCatalogServer[],
  query: string
): McpCatalogServer[] => {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];
  const nameMatches: McpCatalogServer[] = [];
  const descriptionMatches: McpCatalogServer[] = [];
  for (const server of catalog) {
    if (server.name.toLowerCase().includes(needle)) {
      nameMatches.push(server);
    } else if (server.description.toLowerCase().includes(needle)) {
      descriptionMatches.push(server);
    }
  }
  return [...nameMatches, ...descriptionMatches];
};
